import React, { useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import clientDeepLinks from "@/utils/clientDeepLinks";
import log2Sentry from "@/utils/log2Sentry";

import { isInApp } from "@/utils/util";
import Button from "@/components/Button";
import ContentContainer from "@/containers/common/ContentContainer";
import { TextH1, TextH2 } from "@/components/Text";

const initSeries = [
	{
		key: "rockFlow-client-agreement",
		path: "/policy/openAccount/clientAgreement",
	},
	{
		key: "rockFlow-privacy-policy",
		path: "/policy/openAccount/privacyPolicy",
	},
	{
		key: "risk-disclosure-statement",
		path: "/policy/openAccount/riskDisclosureStatement",
	},
	{
		key: "general-disclaimer",
		path: "/policy/openAccount/generalDisclaimer",
	},
	{
		key: "market-data-disclaimer",
		path: "/policy/openAccount/marketDataDisclaimer",
	},
	{
		key: "margin-disclosure-statement",
		path: "/policy/openAccount/marginDisclosureStatement",
	},
	{
		key: "information-disclaimer",
		path: "/policy/openAccount/informationDisclaimer",
	},
	{
		key: "client-declarations",
		path: "/policy/openAccount/clientDeclarations",
	},
	{
		key: "w-8ben",
		path: "/policy/openAccount/w-8ben",
		other: true,
	},
	{
		key: "crs-self-c-form",
		path: "/policy/openAccount/crs",
		other: true,
	},
];

export default function PolicyView({
	onSubmit,
	extraSeries = [],
}: {
	onSubmit: () => Promise<void>;
	extraSeries?: { key: string; path: string; other?: boolean }[];
}) {
	const router = useRouter();
	const [loading, setLoading] = useState(false);

	const { t } = useTranslation("policy");

	const handleAgreement = async () => {
		try {
			setLoading(true);
			await onSubmit();
		} catch (error) {
			log2Sentry(error);
			setLoading(false);
		}
	};

	const handleDirect = (path: string, other?: boolean) => {
		if (isInApp()) {
			const relatePath = `${window.location.origin}/${router.locale}${path}?c=true`;
			const linkUrl = clientDeepLinks.webview(relatePath);

			window.location.href = linkUrl;
		} else {
			if (other) {
				window.location.href = `${window.location.origin}/${router.locale}${path}`;
			} else {
				router.push(path);
			}
		}
	};

	return (
		<ContentContainer
			animation
			footer={
				<footer className="py-4.5 px-4.5">
					<Button onClick={handleAgreement} block loading={loading}>
						{t("confirm-and-agree")}
					</Button>
				</footer>
			}
			header={
				<div className="pt-6 px-4.5">
					<TextH1 className="text-[1.5rem] rf-page-ani rf-page-ani-delay-0">
						{t("general-agreements-and-disclosures")}
					</TextH1>
					<TextH2 className="mt-4 rf-page-ani rf-page-ani-delay-0">{t("policy-click-to-view-the-details")}</TextH2>
				</div>
			}
		>
			<div className="h-9" />
			<div className="mx-4.5 px-4.5 py-3 rounded-lg rf-bg-secondary _717275_94_text">
				{[...extraSeries, ...initSeries].map((item) => (
					<a
						id={item.key}
						key={item.key}
						className="py-3 block border-b-1 border-b-gray_E7/50 dark:border-b-[#212121]/[50%] last:border-b-0"
						onClick={() => handleDirect(item.path, item.other)}
					>
						{t(item.key)}
					</a>
				))}
			</div>
		</ContentContainer>
	);
}
